/**
 * Scheduler V1 Types
 *
 * TypeScript interfaces matching the backend Pydantic models for the
 * scheduling workflow (Scheduler -> LC pipeline).
 */

import type { TroubleTicketEntry } from '../components/TroubleTicketBadge';

export type { TroubleTicketEntry };

// ============================================================================
// Enums
// ============================================================================

export type ScheduleStatus = 'ALL' | 'PLANNING' | 'PENDING' | 'PENDING_APPT' | 'RETURNED' | 'RESCHEDULED' | 'HOT_ORDER' | 'FINALIZED' | 'HOLD' | 'PENDING_CUSTOMER';

export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  ALL: 'All',
  PLANNING: 'Planning',
  PENDING: 'Pending LC',
  PENDING_APPT: 'Pending Appt',
  RETURNED: 'Returned',
  RESCHEDULED: 'Rescheduled',
  HOT_ORDER: 'Hot Order',
  FINALIZED: 'Finalized',
  HOLD: 'Hold',
  PENDING_CUSTOMER: 'Pending Customer',
};

export const SCHEDULE_STATUS_COLORS: Record<ScheduleStatus, string> = {
  ALL: 'bg-gray-100 text-gray-800',
  PLANNING: 'bg-blue-100 text-blue-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  PENDING_APPT: 'bg-purple-100 text-purple-800',
  RETURNED: 'bg-red-100 text-red-800',
  RESCHEDULED: 'bg-orange-100 text-orange-800',
  HOT_ORDER: 'bg-red-100 text-red-800',
  FINALIZED: 'bg-green-100 text-green-800',
  HOLD: 'bg-gray-100 text-gray-800',
  PENDING_CUSTOMER: 'bg-pink-100 text-pink-800',
};

export type LCNextStep = 'PENDING_LC' | 'PENDING_CUSTOMER' | 'HOLD';

export interface RescheduleEntry {
  reason: string;
  old_date: string;
  new_date: string;
  created: string;
}

export interface RescheduleHistoryItem {
  recno: number;
  pronumber: number;
  createdatetime: string | null;
  createdby: string | null;
  coded_by: string | null;
  servicefailuredate: string | null;
  olddate: string | null;
  newdate: string | null;
  reason: string | null;
  /** What failed — one of SERVICE_FAILURE_TYPES (constants/serviceFailureTypes) or ''. */
  servicefailuretype: string | null;
  thd_reason: string | null;
  servicefailure: string | null;
  responsibledriver: string | null;
  comments: string | null;
  /** Reviewed stamp — "reviewed" is simply a non-blank reviewedby. */
  reviewedby: string | null;
  revieweddatetime: string | null;
}

/**
 * One work-item row for the combined TTs & Service Failures (LC resolution)
 * page. Either troubleticketrecno or reschedulerecno is set (both when a
 * ticket and reschedule are linked). Trouble-ticket fields are prefixed `tt_`
 * / carried as troubleticketrecno; reschedule fields keep their column names.
 */
export interface LcResolutionItem {
  pronumber: number;
  bookingnumber: string | null;
  customer: string | null;
  pickup: string | null;
  delivery: string | null;
  csr: string | null; // order.csr (the LC)
  container: string | null; // order.container (container number)
  pro_bol: string | null; // order.bol for this row's pro (blank on booking-only rows)
  traceterminatedatetime: string | null; // order.traceterminatedatetime
  // Trouble-ticket side
  troubleticketrecno: number | null;
  tt_userid: string | null;
  tt_datetime: string | null;
  category: string | null;
  tt_status: string | null;
  assignedto: string | null;
  issue: string | null;
  preventable: string | null;
  driverticket: string | null;
  servicefailure: string | null;
  resolve: string | null;
  // Reschedule side
  reschedulerecno: number | null;
  createdby: string | null;
  coded_by: string | null;
  createdatetime: string | null;
  servicefailuredate: string | null;
  olddate: string | null; // reschedule.olddate — the appointment date the SF moved off of (YYYY-MM-DD)
  reason: string | null;
  servicefailuretype: string | null; // what failed — SERVICE_FAILURE_TYPES or '' (untyped)
  thd_reason: string | null;
  responsibledriver: string | null;
  comments: string | null;
  // Review (report page). `reviewed` is DERIVED by the backend, not stored: 'Y'
  // only when every record the row carries (ticket and/or SF) has a reviewedby;
  // reviewer / time are the more recent stamp of the two. Saving the checkbox
  // writes both records (the backend stamps who / when).
  reviewed: string | null; // 'Y' | 'N'
  reviewedby: string | null;
  revieweddatetime: string | null;
}

// ============================================================================
// Request Types
// ============================================================================

export interface LegScheduleUpdate {
  proposed_appt_date?: string | null;
  proposed_prepull_date?: string | null;
  appt_time_start?: string | null;
  appt_time_end?: string | null;
  lc_next_step?: LCNextStep | null;
  scheduling_comments?: string | null;
  scheduling_note?: string | null;
  schedule_status?: ScheduleStatus;
  target_delivery_date?: string | null;
  target_on_or_by?: string | null;
}

export interface ProReference {
  pronumber: number;
}

// Alias for backwards compatibility
export type LegReference = ProReference;

export interface SendToLCRequest {
  legs: LegReference[];
}

export interface ReturnToSchedulerRequest extends SendToLCRequest {
  /**
   * YYYY-MM-DD. Required of callers holding a full grant on the
   * `lc-return-target-date` component (the LCs) and rejected server-side if
   * they omit it; optional for everyone else.
   */
  target_delivery_date?: string;
  /**
   * Qualifies the date — deliver ON it vs BY it. Required whenever
   * target_delivery_date is sent (a date without it is unsaveable on the PRO
   * detail drawer, Rule 12) and must be omitted without one.
   */
  target_on_or_by?: 'ON' | 'BY';
}

// ============================================================================
// Response Types
// ============================================================================

export interface SchedulingLeg {
  prepull_needed?: boolean;
  confirmed_delivery_date?: string | null;
  prepull_holds?: string[];
  id: number | null;
  pronumber: number;
  leg_num: number;
  proposed_appt_date: string | null;
  proposed_prepull_date: string | null;
  prepull_from_cms: boolean;
  appt_time_start: string | null;
  appt_time_end: string | null;
  schedule_status: ScheduleStatus | null;
  lc_next_step: LCNextStep | null;
  scheduling_comments: string | null;

  // Order fields
  customer: string | null;
  customercode: number | null;
  move_type: string | null;
  mode: string | null;
  equip_type: string | null;

  // Leg fields
  orig_code: string | null;
  dest_code: string | null;
  orig_name: string | null;
  dest_name: string | null;
  leg_type: string | null;  // Origin action (PICKUP, DELIVERY, etc.)
  dest_action: string | null;  // Destination action (STOPOFF, DELIVERY, etc.)

  // Critical dates
  last_free_day: string | null;
  cutoff_date: string | null;
  per_diem_date: string | null;

  // Equipment flags
  is_hazmat: boolean;
  is_reefer: boolean;
  is_93k: boolean;
  is_daycab: boolean;
  is_tank: boolean;
  is_overweight: boolean;
  overweight_approval_pending?: boolean;
  is_hot: boolean;
  is_triaxle: boolean;
  is_swing: boolean;
  is_early: boolean;
  is_ppe_required?: boolean;

  // Flags
  is_reschedule_required: boolean;
  is_missing_weight: boolean;
  /** Haz=Y with no live HAZ-MAT PAPERWORK document — Finalize is blocked on it. */
  is_missing_haz_docs?: boolean;
  is_cod: boolean;
  inbond_type: string | null;
  reschedule_count: number;
  reschedule_history: RescheduleEntry[];

  // Active (NEW/PENDING) trouble tickets for the TT column
  trouble_ticket_count: number;
  trouble_tickets: TroubleTicketEntry[];

  // Delivery type
  delivery_type: string | null;

  // Mileage
  miles: number | null;
  mileage_band?: number | null;

  // Personnel
  logistics_coordinator: string | null;
  dispatcher_name: string | null;

  // Order reference
  bol: string | null;
  scac: string | null;
  po: string | null;
  container_number: string | null;
  bill_to_ref: string | null;

  // Destination address
  dest_city: string | null;
  dest_state: string | null;
  dest_zip: string | null;

  // Bill to
  bill_to_name: string | null;

  // Dates
  available_date: string | null;
  eta_date: string | null;

  // Terminal
  terminal: string | null;

  // Rail pickup number (from originating leg)
  rail_pickup_number: string | null;

  // Origin location type (e.g., PORT, RAIL YARD)
  orig_location_type: string | null;

  // Whether origin location requires a pickup number (Y/N)
  orig_require_pickup: string | null;

  // Whether order has a prepull leg group (PICKUP to non-delivery with a drop before delivery)
  has_prepull_leg: boolean;

  // Pro comments from orderscurrent
  pro_comments: string | null;

  // Drop indicator
  is_drop: boolean;

  // Key account status (from customers table)
  key_account: string | null;

  // Storage cleared date
  storage_cleared_date: string | null;

  // Whether the PRO has a live storage Additional Cost record (STORAGE / STORAGE - CC).
  // Required alongside storage_cleared_date for the purple row highlight.
  has_storage_addl_cost?: boolean;

  // Target delivery date (editable by LC)
  target_delivery_date: string | null;

  // Target on or by (ON/BY)
  target_on_or_by: string | null;

  // Customer target date (deliveryoption1date)
  customer_target_date: string | null;

  // First receiving date
  first_rcv_date: string | null;

  // Prepull finalization tracking
  prepull_finalized: boolean;

  // Timestamp when this PRO was sent to LC (ISO string from backend)
  sent_to_lc_at: string | null;
  /** Who last set the status to RETURNED ('SYSTEM' = auto-return) */
  returned_by?: string | null;

  // Location hours
  pickup_hours: string | null;
  delivery_hours: string | null;
  pickup_max_lane_capacity: number | null;
  delivery_max_lane_capacity: number | null;
  pickup_max_deliveries: number | null;
  delivery_max_deliveries: number | null;

  // Sub-rows for hierarchical display (legs under orders)
  subRows?: SchedulingLeg[];

  // All legs for this order (used with renderExpandedContent for custom panels)
  allLegs?: SchedulingLeg[];

  // For parent rows: pickup = first leg origin, delivery = last leg destination
  pickup_name?: string | null;
  delivery_name?: string | null;
}

export interface RunningTotalDay {
  date: string;
  scheduled_count: number;
  capacity: number;
  percentage: number;
}

export interface RunningTotalsResponse {
  days: RunningTotalDay[];
}

export interface FilterOption {
  value: string;
  label: string;
  /** LC usernames associated with this option (customers only) */
  lcs?: string[];
}

export interface FilterOptionsResponse {
  terminals: string[];
  customers: FilterOption[];
  pickup_locations: string[];
  delivery_locations: string[];
  logistics_coordinators: FilterOption[];
}

// ============================================================================
// Filter State
// ============================================================================

export interface SchedulerFilters {
  include_prepull_needed?: boolean;
  move_type: 'ALL' | 'IMPORT' | 'EXPORT' | 'DOMESTIC';
  terminal?: string[];
  delivery_type?: string;
  status?: ScheduleStatus[];
  customer?: string[];
  logistic_coordinator?: string[];
  pickup_from?: string;
  deliver_to?: string;
  restriction?: string;
  prepull_filter?: string;
  mileage_band?: number;
  appt_date?: string;
  pronumber?: number;
  include_null_status?: boolean;
}

// ============================================================================
// Table Selection State
// ============================================================================

export interface SchedulerSelection {
  selectedLegs: Set<string>; // pronumber as string
}

// ============================================================================
// History Types
// ============================================================================

export interface SchedulingHistoryRecord {
  id: number;
  pronumber: number;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  action_type: string;
  changed_by: string;
  changed_at: string;
  notes: string | null;
  /**
   * Which log the row came from: 'scheduling' = the board's own history,
   * 'cms' = a target-delivery-date set/cleared event from CMS history (covers
   * edits made in V1, the 360 order form, cmsapi or EDI). `id` is unique only
   * within a source — row keys must combine the two.
   */
  source: 'scheduling' | 'cms';
}

// ============================================================================
// Driver Availability Types
// ============================================================================

export interface TrucksBreakdownByStatus {
  planning: number;
  pending: number;
  finalized: number;
  total: number;
}

export interface TrucksBreakdown {
  prepulls: TrucksBreakdownByStatus;
  deliveries: TrucksBreakdownByStatus;
  returns?: TrucksBreakdownByStatus;
  delivery_mileage_bands?: Record<string, TrucksBreakdownByStatus>;
}

export interface DriverStatusBreakdown {
  available: number;
  accepted: number;
  accepted_confirmed: number;
  planned: number;
  in_route_otr: number;
  not_working: number;
  out_of_service: number;
  unknown: number;
}

export interface DriverAvailabilityDay {
  date: string;
  working_count: number;
  trucks_required: number;
  breakdown?: TrucksBreakdown | null;
  driver_breakdown: DriverStatusBreakdown;
}

export interface DriverAvailabilityResponse {
  local: DriverAvailabilityDay[];
  otr: DriverAvailabilityDay[];
}

// ============================================================================
// Terminal Settings Types
// ============================================================================

export interface TerminalSettings {
  id: number;
  terminal: string;
  truck_pool_type: 'Separate' | 'Combined';
  local_deliveries_per_day: number;
  prepulls_per_day: number;
  accepting_new_lanes: boolean;
  prepull_type: 'Local' | 'OTR';
}

export interface TerminalSettingsUpdate {
  truck_pool_type?: 'Separate' | 'Combined';
  local_deliveries_per_day?: number;
  prepulls_per_day?: number;
  accepting_new_lanes?: boolean;
  prepull_type?: 'Local' | 'OTR';
}
